import { Link, useLocation } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  Home,
  Box,
  Notebook,
  Code,
  Global,
  ShopMinimalistic,
  Shop,
  Leaf,
  Buildings,
  UndoLeftRound,
  Restart,
  TagPrice,
  ClipboardList,
  Dollar,
  GraphUp,
  Route,
  Safe2,
  Bolt,
  Bell,
  Flag,
  Key,
  DocumentText,
  WadOfMoney,
  Settings,
  AltArrowDown,
  CheckCircle,
  AddCircle,
} from "@solar-icons/react";
import { useAuth } from "../lib/auth-context";
import { useTenant } from "../lib/tenant-context";

interface Program {
  id: string;
  name: string;
  type: "takeback" | "trade_in" | "resale"; // API returns 'type' not 'program_type'
  status: string;
}

interface ProgramsByType {
  takeback: Program[];
  trade_in: Program[];
  resale: Program[];
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { currentTenant, setTenantId, availableTenants } = useTenant();
  const location = useLocation();
  const [programs, setPrograms] = useState<ProgramsByType>({
    takeback: [],
    trade_in: [],
    resale: [],
  });
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["takeback", "resale"]),
  );
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);

  // Use tenant from context instead of local state
  const selectedTenantId = currentTenant.id;
  const selectedTenant = currentTenant;

  const handleTenantChange = (tenantId: string) => {
    setTenantId(tenantId);
    setIsTenantDropdownOpen(false);
    // Dispatch custom event for components to listen
    window.dispatchEvent(
      new CustomEvent("tenantChanged", { detail: { tenantId } }),
    );
    // Reload page to reflect tenant change
    window.location.reload();
  };

  // Fetch programs on mount and when tenant changes
  useEffect(() => {
    fetch(`/api/programs?tenantId=${encodeURIComponent(selectedTenantId)}`)
      .then((res) => res.json())
      .then((data) => {
        const byType: ProgramsByType = {
          takeback: [],
          trade_in: [],
          resale: [],
        };
        (data.programs || []).forEach((p: Program) => {
          if (byType[p.type]) {
            byType[p.type].push(p);
          }
        });
        setPrograms(byType);
      })
      .catch(() => {});
  }, [selectedTenantId]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const isActive = (href: string) => {
    return (
      location.pathname === href ||
      (href !== "/" && location.pathname.startsWith(href))
    );
  };

  const isProgramActive = (programId: string) => {
    return location.pathname.includes(programId);
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#FAFAFA" }}>
      {/* Sidebar */}
      <aside
        className="w-64 border-r flex flex-col fixed h-screen"
        style={{
          backgroundColor: "#FFFFFF",
          borderColor: "rgba(169,169,169,0.2)",
        }}
      >
        {/* Logo */}
        <div
          className="h-16 px-6 flex items-center border-b"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          <Link to="/" className="flex items-center group">
            <img
              src="/images/pentatonic-logo.svg"
              alt="Pentatonic"
              className="h-5"
              style={{ width: "auto" }}
            />
          </Link>
        </div>

        {/* Tenant Selector */}
        <div
          className="px-4 py-4 border-b relative"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          <button
            onClick={() =>
              availableTenants.length > 1 &&
              setIsTenantDropdownOpen(!isTenantDropdownOpen)
            }
            className={`w-full flex items-center gap-3 px-4 py-2 transition-colors text-left ${
              availableTenants.length > 1
                ? "hover:bg-penta-grey-light cursor-pointer"
                : "cursor-default"
            }`}
          >
            <div
              className="w-8 h-8 flex items-center justify-center flex-shrink-0 overflow-hidden"
              style={{ backgroundColor: selectedTenant.color }}
            >
              {selectedTenant.id === "nike" ? (
                <img
                  src="/images/nike/swoosh-white.svg"
                  alt="Nike"
                  className="w-5 h-5 object-contain"
                />
              ) : (
                <span className="font-medium text-sm text-white">
                  {selectedTenant.initial}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate" style={{ color: "#171717" }}>
                {selectedTenant.name}
              </p>
              <p className="text-sm font-mono font-extralight tracking-widest uppercase" style={{ color: "#A9A9A9" }}>
                {selectedTenant.plan}
              </p>
            </div>
            {/* Only show dropdown arrow if user can switch tenants */}
            {availableTenants.length > 1 && (
              <AltArrowDown
                size={16}
                weight="LineDuotone"
                className={`transition-transform ${isTenantDropdownOpen ? "rotate-180" : ""}`}
              />
            )}
          </button>

          {/* Dropdown Menu - only show if user can access multiple tenants */}
          {isTenantDropdownOpen && availableTenants.length > 0 && (
            <div
              className="absolute top-full left-4 right-4 mt-2 bg-white border z-50"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              {availableTenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantChange(tenant.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-penta-grey-light transition-colors text-left ${
                    selectedTenantId === tenant.id ? "bg-penta-grey-light" : ""
                  }`}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: tenant.color }}
                  >
                    {tenant.id === "nike" ? (
                      <img
                        src="/images/nike/swoosh-white.svg"
                        alt="Nike"
                        className="w-5 h-5 object-contain"
                      />
                    ) : (
                      <span className="font-medium text-sm text-white">
                        {tenant.initial}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm truncate"
                      style={{ color: "#171717" }}
                    >
                      {tenant.name}
                    </p>
                    <p
                      className="text-sm font-mono"
                      style={{ color: "#A9A9A9" }}
                    >
                      {tenant.plan}
                    </p>
                  </div>
                  {selectedTenantId === tenant.id && (
                    <CheckCircle size={16} weight="LineDuotone" color="#22c55e" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 overflow-y-auto">
          {/* UNDERSTAND Section */}
          <div className="mb-6">
            <p
              className="px-4 py-2 text-sm font-mono font-extralight tracking-widest"
              style={{ color: "#A9A9A9" }}
            >
              UNDERSTAND
            </p>
            {user?.tenantPlan !== "free" && (
              <NavItem
                href="/"
                icon={<Home size={20} weight="LineDuotone" />}
                label="Overview"
                isActive={location.pathname === "/"}
              />
            )}
            <NavItem
              href="/products"
              icon={<Box size={20} weight="LineDuotone" />}
              label="Products"
              isActive={isActive("/products")}
            />
            <NavItem
              href="/knowledge"
              icon={<Notebook size={20} weight="LineDuotone" />}
              label="Knowledge Hub"
              isActive={isActive("/knowledge")}
            />
            <NavItem
              href="/docs"
              icon={<Code size={20} weight="LineDuotone" />}
              label="Intelligence APIs"
              isActive={isActive("/docs")}
            />
            <NavItem
              href="/market-intelligence"
              icon={<Global size={20} weight="LineDuotone" />}
              label="Market Intelligence"
              isActive={isActive("/market-intelligence")}
            />
          </div>

          {/* MANAGE Section - Dynamic based on programs */}
          <div className="mb-6">
            <p
              className="px-4 py-2 text-sm font-mono font-extralight tracking-widest"
              style={{ color: "#A9A9A9" }}
            >
              MANAGE
            </p>

            {/* Physical Intelligence */}
            <NavItem
              href="/pin"
              icon={<ShopMinimalistic size={20} weight="LineDuotone" />}
              label="Physical Intelligence"
              isActive={isActive("/pin")}
            />

            {/* Shopify Trade-Ins */}
            <NavItem
              href="/shopify-trade-ins"
              icon={<Shop size={20} weight="LineDuotone" />}
              label="Shopify Trade-Ins"
              isActive={isActive("/shopify-trade-ins")}
            />

            {/* Nespresso Recycling Reports - only show for Nespresso tenant */}
            {selectedTenantId === "nespresso" && (
              <NavItem
                href="/nespresso-analytics"
                icon={<Leaf size={20} weight="LineDuotone" />}
                label="Recycling Reports"
                isActive={isActive("/nespresso-analytics")}
              />
            )}

            {/* Warehouse - show for tenants with warehouse access */}
            {(selectedTenantId === "nespresso" ||
              selectedTenantId === "adidas") && (
              <NavItem
                href="/warehouse"
                icon={<Buildings size={20} weight="LineDuotone" />}
                label="Warehouse"
                isActive={isActive("/warehouse")}
              />
            )}

            {/* Takeback Programs */}
            {programs.takeback.length > 0 && (
              <ProgramSection
                label="Takeback"
                icon={<UndoLeftRound size={20} weight="LineDuotone" />}
                programs={programs.takeback}
                expanded={expandedSections.has("takeback")}
                onToggle={() => toggleSection("takeback")}
                isProgramActive={isProgramActive}
                currentPath={location.pathname}
                subPages={[
                  { label: "Dashboard", path: "/dashboard" },
                  { label: "Takebacks", path: "/takebacks" },
                  { label: "Analytics", path: "/analytics" },
                ]}
              />
            )}

            {/* Trade-in Programs */}
            {programs.trade_in.length > 0 && (
              <ProgramSection
                label="Trade-in"
                icon={<Restart size={20} weight="LineDuotone" />}
                programs={programs.trade_in}
                expanded={expandedSections.has("trade_in")}
                onToggle={() => toggleSection("trade_in")}
                isProgramActive={isProgramActive}
                currentPath={location.pathname}
                subPages={[
                  { label: "Dashboard", path: "/dashboard" },
                  { label: "Trade-ins", path: "/takebacks" },
                  { label: "Analytics", path: "/analytics" },
                ]}
              />
            )}

            {/* Resale Programs */}
            {programs.resale.length > 0 && (
              <ProgramSection
                label="Resale"
                icon={<TagPrice size={20} weight="LineDuotone" />}
                programs={programs.resale}
                expanded={expandedSections.has("resale")}
                onToggle={() => toggleSection("resale")}
                isProgramActive={isProgramActive}
                currentPath={location.pathname}
                subPages={[
                  { label: "Dashboard", path: "/dashboard" },
                  { label: "Listings", path: "" },
                  { label: "Analytics", path: "/analytics" },
                ]}
              />
            )}

            {/* Fallback if no programs */}
            {programs.takeback.length === 0 &&
              programs.trade_in.length === 0 &&
              programs.resale.length === 0 && (
                <Link
                  to="/programs"
                  className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                  style={{ color: "#171717" }}
                >
                  <AddCircle size={20} weight="LineDuotone" />
                  <span>Create Program</span>
                </Link>
              )}
          </div>

          {/* ANALYTICS Section - only show for Arc'teryx tenant */}
          {selectedTenantId === "arcteryx" && (
            <div className="mb-6">
              <p
                className="px-4 py-2 text-sm font-mono font-extralight tracking-widest"
                style={{ color: "#A9A9A9" }}
              >
                ANALYTICS
              </p>
              <NavItem
                href="/inventory"
                icon={<ClipboardList size={20} weight="LineDuotone" />}
                label="Inventory"
                isActive={isActive("/inventory")}
              />
              <NavItem
                href="/pricing-engine"
                icon={<Dollar size={20} weight="LineDuotone" />}
                label="Pricing Engine"
                isActive={isActive("/pricing-engine")}
              />
              <NavItem
                href="/trade-in-analytics"
                icon={<GraphUp size={20} weight="LineDuotone" />}
                label="Trade-In Reports"
                isActive={isActive("/trade-in-analytics")}
              />
            </div>
          )}

          {/* CONFIGURE Section */}
          <div className="mb-6">
            <p
              className="px-4 py-2 text-sm font-mono font-extralight tracking-widest"
              style={{ color: "#A9A9A9" }}
            >
              CONFIGURE
            </p>
            <NavItem
              href="/stores"
              icon={<Shop size={20} weight="LineDuotone" />}
              label="Stores & Brands"
              isActive={isActive("/stores")}
            />
            <NavItem
              href="/programs"
              icon={<Route size={20} weight="LineDuotone" />}
              label="Programs"
              isActive={isActive("/programs") || isActive("/route-tester")}
            />
            <NavItem
              href="/treasury"
              icon={<Safe2 size={20} weight="LineDuotone" />}
              label="Treasury"
              isActive={isActive("/treasury")}
            />
            <NavItem
              href="/x402-usage"
              icon={<Bolt size={20} weight="LineDuotone" />}
              label="x402 Usage"
              isActive={isActive("/x402-usage")}
            />
            <NavItem
              href="/campaigns"
              icon={<Bell size={20} weight="LineDuotone" />}
              label="Takeback Campaigns"
              isActive={isActive("/campaigns")}
            />
            <NavItem
              href="/sponsored"
              icon={<Flag size={20} weight="LineDuotone" />}
              label="Sponsored Products"
              isActive={isActive("/sponsored")}
            />
          </div>

          {/* BUILD Section */}
          <div className="mb-6">
            <p
              className="px-4 py-2 text-sm font-mono font-extralight tracking-widest"
              style={{ color: "#A9A9A9" }}
            >
              BUILD
            </p>
            <NavItem
              href="/api-keys"
              icon={<Key size={20} weight="LineDuotone" />}
              label="API Keys"
              isActive={isActive("/api-keys")}
            />
            <NavItem
              href="/docs/getting-started"
              icon={<DocumentText size={20} weight="LineDuotone" />}
              label="Documentation"
              isActive={isActive("/docs/getting-started")}
            />
          </div>

          {/* Admin-only section */}
          {user?.isAdmin && (
            <div className="mb-6">
              <p
                className="px-4 py-2 text-sm font-mono font-extralight tracking-widest"
                style={{ color: "#A9A9A9" }}
              >
                INTERNAL
              </p>
              <NavItem
                href="/revenue"
                icon={<WadOfMoney size={20} weight="LineDuotone" />}
                label="Revenue"
                isActive={isActive("/revenue")}
              />
            </div>
          )}
        </nav>

        {/* Footer */}
        <div
          className="px-4 py-4 border-t"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          {/* Settings Link */}
          <Link
            to="/settings"
            className="flex items-center gap-3 px-4 py-2 text-sm transition-colors mb-3"
            style={isActive("/settings") ? { backgroundColor: "#FAFAFA", color: "#171717" } : { color: "#171717" }}
          >
            <span style={{ color: isActive("/settings") ? "#3941EB" : "currentColor" }}>
              <Settings size={20} weight="LineDuotone" />
            </span>
            Settings
          </Link>

          {user && (
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: currentTenant.color }}
              >
                <span className="text-sm text-white font-medium">
                  {user.tenantName[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate" style={{ color: "#171717" }}>
                  {user.tenantName}
                  {user.isAdmin && (
                    <span className="ml-1 text-sm" style={{ color: "#00FBA9" }}>
                      Admin
                    </span>
                  )}
                </p>
                <button
                  onClick={logout}
                  className="text-sm transition-colors hover:underline"
                  style={{ color: "#A9A9A9" }}
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 ml-64 flex flex-col">
        {/* Top bar */}
        <header
          className="h-16 border-b px-8 flex items-center justify-between sticky top-0 z-10"
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "rgba(169,169,169,0.1)",
          }}
        >
          <div>{/* Breadcrumb or page title can go here */}</div>
          <div className="flex items-center gap-4">
            <Link
              to="/docs/getting-started"
              className="text-sm transition-colors"
              style={{ color: "#A9A9A9" }}
            >
              Help
            </Link>
          </div>
        </header>

        {/* Page content */}
        <div className="p-8 flex-1 flex flex-col" style={{ backgroundColor: "#FFFFFF" }}>{children}</div>
      </main>
    </div>
  );
}

// Navigation Item Component
function NavItem({
  href,
  icon,
  label,
  isActive,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}) {
  return (
    <Link
      to={href}
      className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
      style={isActive ? { backgroundColor: "#FAFAFA", color: "#171717" } : { color: "#171717" }}
    >
      <span style={{ color: isActive ? "#3941EB" : "currentColor" }}>{icon}</span>
      {label}
    </Link>
  );
}

// Program Section with Collapsible Sub-navigation
function ProgramSection({
  label,
  icon,
  programs,
  expanded,
  onToggle,
  isProgramActive,
  subPages,
  currentPath,
}: {
  label: string;
  icon: React.ReactNode;
  programs: Program[];
  expanded: boolean;
  onToggle: () => void;
  isProgramActive: (id: string) => boolean;
  subPages: { label: string; path: string }[];
  currentPath: string;
}) {
  const location = { pathname: currentPath };
  const hasActiveProgram = programs.some((p) => isProgramActive(p.id));

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left"
        style={hasActiveProgram ? { backgroundColor: "#FAFAFA", color: "#171717" } : { color: "#171717" }}
      >
        <span style={{ color: hasActiveProgram ? "#3941EB" : "currentColor" }}>{icon}</span>
        <span className="flex-1">{label}</span>
        <span
          className="transition-transform"
          style={{ transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }}
        >
          <AltArrowDown size={16} weight="LineDuotone" />
        </span>
      </button>

      {expanded && (
        <div className="ml-6 mt-1 space-y-0.5">
          {programs.length === 1
            ? // Single program - show sub-pages directly
              subPages.map((subPage) => {
                const programId = programs[0].id;
                const fullPath = subPage.path
                  ? `/programs/${programId}/${subPage.path.replace(/^\//, "")}`
                  : `/programs/${programId}`;
                const isSubPageActive =
                  location.pathname === fullPath ||
                  (subPage.path === "" &&
                    location.pathname === `/programs/${programId}`);
                return (
                  <Link
                    key={subPage.path || "main"}
                    to={fullPath}
                    className="flex items-center gap-2 px-4 py-1.5 text-sm transition-colors"
                    style={isSubPageActive ? { backgroundColor: "#FAFAFA", color: "#171717" } : { color: "#171717" }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor: isSubPageActive ? "#3941EB" : "#D1D1D1",
                      }}
                    />
                    {subPage.label}
                  </Link>
                );
              })
            : // Multiple programs - show program list
              programs.map((program) => (
                <Link
                  key={program.id}
                  to="/programs/$id"
                  params={{ id: program.id }}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm transition-colors"
                  style={isProgramActive(program.id) ? { backgroundColor: "#FAFAFA", color: "#171717" } : { color: "#171717" }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: isProgramActive(program.id) ? "#3941EB" : "#D1D1D1",
                    }}
                  />
                  {program.name}
                </Link>
              ))}
        </div>
      )}
    </div>
  );
}

